/**
 * Unified Filter Framework - Core Types
 *
 * This module defines the type system for the search-based filtering system.
 * Inspired by Linear.app's query builder pattern.
 */

import type { GraphDataNode, NodeTypeId, GraphDomain } from '../types/graph.types';

/** Back-compat alias used elsewhere in jnana-web. Same shape as GraphDataNode. */
export type FilterableNode = GraphDataNode;

// ============================================================================
// Filter Operators
// ============================================================================

/**
 * Operators that can be applied to filter values
 */
export enum FilterOperator {
  // String operators
  Is = 'is',
  Contains = 'contains',
  StartsWith = 'startsWith',
  EndsWith = 'endsWith',
  NotIs = 'notIs',
  NotContains = 'notContains',
  SoundsLike = 'soundsLike', // Phonetic matching

  // Numeric operators
  Equals = 'equals',
  GreaterThan = 'greaterThan',
  LessThan = 'lessThan',
  GreaterThanOrEqual = 'greaterThanOrEqual',
  LessThanOrEqual = 'lessThanOrEqual',

  // Collection operators
  In = 'in',
  NotIn = 'notIn',

  // Existence operators (for edges/relationships)
  Has = 'has',
  NotHas = 'notHas',
  HasAny = 'hasAny',
  HasNone = 'hasNone',
  HasSpecific = 'hasSpecific', // Has specific node(s) in edge
  HasCount = 'hasCount', // Has edge with count comparison
}

/**
 * Human-readable labels for operators
 */
export const FilterOperatorLabels: Record<FilterOperator, string> = {
  [FilterOperator.Is]: 'is exactly',
  [FilterOperator.Contains]: 'contains',
  [FilterOperator.StartsWith]: 'starts with',
  [FilterOperator.EndsWith]: 'ends with',
  [FilterOperator.NotIs]: 'is not',
  [FilterOperator.NotContains]: 'does not contain',
  [FilterOperator.SoundsLike]: 'sounds like',
  [FilterOperator.Equals]: 'equals',
  [FilterOperator.GreaterThan]: 'greater than',
  [FilterOperator.LessThan]: 'less than',
  [FilterOperator.GreaterThanOrEqual]: 'at least',
  [FilterOperator.LessThanOrEqual]: 'at most',
  [FilterOperator.In]: 'is one of',
  [FilterOperator.NotIn]: 'is not one of',
  [FilterOperator.Has]: 'has',
  [FilterOperator.NotHas]: 'does not have',
  [FilterOperator.HasAny]: 'has any',
  [FilterOperator.HasNone]: 'has none',
  [FilterOperator.HasSpecific]: 'has specific',
  [FilterOperator.HasCount]: 'has count',
};

/**
 * Count comparison operators for edge "has count" filters.
 * Shared by EdgeFilterValue.countComparison.operator and the EdgeFilterConfig
 * count-operator state so the union lives in one place.
 */
export type CountOperator =
  | 'equals'
  | 'greaterThan'
  | 'lessThan'
  | 'greaterThanOrEqual'
  | 'lessThanOrEqual';

// ============================================================================
// Filter Value Types
// ============================================================================

/**
 * Types of values that filters can work with
 */
export enum FilterValueType {
  String = 'string',
  Number = 'number',
  NodeId = 'nodeId',
  NodeType = 'nodeType',
  Domain = 'domain',
  Boolean = 'boolean',
  NodeIdArray = 'nodeIdArray',
  EdgeName = 'edgeName',
}

// ============================================================================
// Filter Definition
// ============================================================================

/**
 * Configuration props passed to custom filter config components
 */
export interface FilterConfigProps {
  filter: ActiveFilter;
  definition: FilterDefinition;
  onUpdate: (operator: FilterOperator, value: FilterValue) => void;
  onClose: () => void;
  /** Nodes being filtered - for context-aware config (e.g., edge filter) */
  nodes?: GraphDataNode[];
}

/**
 * Edge filter value - for filtering by relationship with optional target node(s)
 */
export interface EdgeFilterValue {
  /** The edge name (e.g., 'ragam', 'composer') */
  edgeName: string;
  /** Optional target node ID(s) - if specified, filters to nodes with this specific relationship */
  targetNodeIds?: number[];
  /** If true, all target nodes must match (AND logic); if false, any can match (OR logic) */
  matchAll?: boolean;
  /** Optional count comparison - for "has more than X" type filters */
  countComparison?: {
    operator: CountOperator;
    value: number;
  };
}

/**
 * Possible filter values
 */
export type FilterValue =
  | string
  | number
  | boolean
  | NodeTypeId
  | GraphDomain
  | number[]
  | string[]
  | EdgeFilterValue;

/**
 * A suggestion generated from user input
 */
export interface FilterSuggestion {
  /** Reference to the filter definition */
  filterId: string;
  /** Display label for the suggestion (e.g., "Type: Bhajan") */
  label: string;
  /** Optional description for context */
  description?: string;
  /** Pre-selected operator for this suggestion */
  prefilledOperator: FilterOperator;
  /** Pre-filled value (if applicable) */
  prefilledValue: FilterValue;
  /** If true, opens config dialog after selection */
  needsConfiguration: boolean;
  /** Relevance score for ranking (higher = more relevant) */
  score: number;
  /** If true, this is a top-level filter category (not a specific filter value) */
  isCategory?: boolean;
}

/**
 * Definition schema for a filter type
 */
export interface FilterDefinition {
  /** Unique identifier for this filter type */
  id: string;
  /** Display name (e.g., "Node Type") */
  label: string;
  /** Short label for pills (e.g., "Type") */
  shortLabel: string;
  /** Icon name from Heroicons (optional) */
  icon?: string;
  /** Type of value this filter works with */
  valueType: FilterValueType;
  /** Operators supported by this filter */
  supportedOperators: FilterOperator[];
  /** Default operator when creating new filter */
  defaultOperator: FilterOperator;
  /**
   * Generate suggestions based on user query
   * @param query - Current search text
   * @param nodes - All available nodes (for context)
   * @returns Array of suggestions ranked by relevance
   */
  suggestMatch: (query: string, nodes: GraphDataNode[]) => FilterSuggestion[];
  /**
   * Execute the filter against a node
   * @param node - Node to test
   * @param operator - Selected operator
   * @param value - Filter value
   * @returns true if node passes the filter
   */
  execute: (node: GraphDataNode, operator: FilterOperator, value: FilterValue) => boolean;
  /**
   * Format value for display in pills
   * @param value - Filter value
   * @returns Human-readable string
   */
  getDisplayValue?: (value: FilterValue) => string;
  /**
   * Optional custom configuration component
   */
  ConfigComponent?: React.FC<FilterConfigProps>;
  /**
   * Options for enum-like filters (e.g., node types, domains)
   */
  options?: Array<{ value: FilterValue; label: string }>;
  /**
   * Placeholder text for the value input
   */
  placeholder?: string;
}

// ============================================================================
// Active Filter Instance
// ============================================================================

/**
 * An active filter instance with selected operator and value
 */
export interface ActiveFilter {
  /** Unique instance ID (generated UUID) */
  id: string;
  /** Reference to FilterDefinition.id */
  filterId: string;
  /** Selected operator */
  operator: FilterOperator;
  /** Filter value */
  value: FilterValue;
}

/**
 * A collection of active filters (AND logic - all must pass)
 */
export type FilterSet = ActiveFilter[];

// ============================================================================
// Filter Engine Types
// ============================================================================

/**
 * Result from filter execution
 */
export interface FilterResult {
  /** Filtered nodes */
  nodes: GraphDataNode[];
  /** Total count before filtering */
  totalCount: number;
  /** Count after filtering */
  filteredCount: number;
  /** Time taken in ms */
  executionTimeMs: number;
}

/**
 * Options for suggestion generation
 */
export interface SuggestionOptions {
  /** Maximum number of suggestions to return */
  maxSuggestions?: number;
  /** Minimum query length to start suggesting */
  minQueryLength?: number;
  /** Filter definitions to include (default: all) */
  includeFilters?: string[];
  /** Filter definitions to exclude */
  excludeFilters?: string[];
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Props for the main FilterBar component
 */
export interface FilterBarProps {
  /** Current active filters */
  filterSet: FilterSet;
  /** Callback when filter set changes */
  onFilterSetChange: (filterSet: FilterSet) => void;
  /** Limit which filters are available */
  availableFilters?: string[];
  /** Placeholder text for input */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Pre-applied filters that cannot be removed */
  lockedFilters?: ActiveFilter[];
  /** Auto-focus input on mount */
  autoFocus?: boolean;
}

/**
 * Props for FilterPill component
 */
export interface FilterPillProps {
  /** The active filter to display */
  filter: ActiveFilter;
  /** Filter definition */
  definition: FilterDefinition;
  /** Whether this pill can be edited/removed */
  locked?: boolean;
  /** Callback when remove is clicked */
  onRemove?: () => void;
  /** Callback when pill is clicked (for editing) */
  onClick?: () => void;
}

/**
 * Props for SuggestionDropdown component
 */
export interface SuggestionDropdownProps {
  /** Suggestions to display */
  suggestions: FilterSuggestion[];
  /** Filter definitions for labels */
  definitions: Record<string, FilterDefinition>;
  /** Currently highlighted index */
  highlightedIndex: number;
  /** Callback when suggestion is selected */
  onSelect: (suggestion: FilterSuggestion) => void;
  /** Whether dropdown is visible */
  isVisible: boolean;
}

/**
 * Props for FilterConfigDialog component
 */
export interface FilterConfigDialogProps {
  /** The filter being configured (null if closed) */
  filter: ActiveFilter | null;
  /** Filter definition */
  definition: FilterDefinition | null;
  /** Whether creating a new filter or editing existing */
  isNew: boolean;
  /** Callback to apply changes */
  onApply: (filter: ActiveFilter) => void;
  /** Callback to cancel */
  onCancel: () => void;
  /** Nodes being filtered - for context-aware config */
  nodes?: GraphDataNode[];
}
